import { createContext, useCallback, useEffect, useMemo } from 'react';
import useCrops from '../hooks/useCrops';
import useFieldSections from '../hooks/useFieldSections';
import useLayers from '../hooks/useLayers';
import useMapState from '../hooks/useMapState';
import useSectionGroups from '../hooks/useSectionGroups';
import useBaseLayer from '../hooks/useBaseLayer';
import usePatterns from '../hooks/usePatterns';
import useSoilAttributes from '../hooks/useSoilAttributes';
import { SOIL_BOUNDARIES_LAYER_ID } from '../constants/map';

const initState = {
  crops: {},
  fields: {},
  sectionGroups: {},
  mapState: {},
  baseMapState: {},
  layers: {},
  inRowPatterns: {},
  soilAttributes: {},
  resetState: () => {},
};

export const MapContext = createContext(initState);

export const MapContextProvider = ({ children }) => {
  const { resetCrops, ...crops } = useCrops();
  const { resetInRowPatterns, ...patterns } = usePatterns();
  const { resetFields, ...fields } = useFieldSections({
    cropNameToId: crops.cropNameToId,
    cropVarietyIdToCropId: crops.cropVarietyIdToCropId,
    cropMap: crops.availableCrops,
    inRowPatternsMap: patterns.inRowPatternsMap,
  });
  const {
    sectionGroupsHash,
    resetSectionGroups,
    ...sectionGroups
  } = useSectionGroups();

  const { resetLayers, ...layers } = useLayers();
  const soilLayer = layers.layers[SOIL_BOUNDARIES_LAYER_ID];
  const soilAttributes = useSoilAttributes({ data: soilLayer.data });
  const { resetSoilAttributes } = soilAttributes;
  const { resetMapState, ...mapState } = useMapState(
    fields.sectionsHash,
    sectionGroupsHash,
    layers.layers,
    fields,
  );
  const baseMapState = useBaseLayer();

  // Reset the undo/redo state whenever the active field changes
  useEffect(() => {
    fields.undoRedo.reset();
  }, [mapState.activeField?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetState = useCallback(() => {
    resetCrops();
    resetFields();
    resetSectionGroups();
    resetInRowPatterns();
    resetLayers();
    resetSoilAttributes();
    resetMapState();
    fields.undoRedo.reset();
  }, [
    resetCrops,
    resetFields,
    resetSectionGroups,
    resetInRowPatterns,
    resetLayers,
    resetSoilAttributes,
    resetMapState,
    fields.undoRedo,
  ]);

  const value = useMemo(
    () => ({
      crops,
      fields,
      sectionGroups,
      patterns,
      mapState,
      baseMapState,
      layers,
      soilAttributes,
      resetState,
    }),
    [
      baseMapState,
      crops,
      fields,
      layers,
      mapState,
      patterns,
      sectionGroups,
      soilAttributes,
      resetState,
    ],
  );

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};
